import { Particle } from '../ParticleLayer'

// Configuration from specs
const CONFIG = {
    baseSize: 30.0,
    sizeVariance: 10.0,
    fluidity: 0.5,
    speed: 3.0, // Base speed unit
    decay: 0.430,
    particleCount: 70,
    headColor: { r: 255, g: 220, b: 100 }, // Bright Yellow Core (was White)
    tailColor: { r: 200, g: 40, b: 0 },    // Deep Red/Orange Tail
}

export type FireballEffect = {
    kind: 'fireball'
    t: number
    sx: number
    sy: number
    tx: number
    ty: number
    data?: {
        particles: FluidParticle[]
        phase: 'IDLE' | 'CHARGING' | 'FLYING' | 'EXPLODING'
        pos: { x: number, y: number }
        velocity: { x: number, y: number }
        target: { x: number, y: number }
        sprite?: HTMLCanvasElement
    }
}

class FluidParticle {
    x: number
    y: number
    vx: number
    vy: number
    life: number
    maxLife: number
    size: number
    heat: number
    drag: number

    constructor(x: number, y: number, vx: number, vy: number, maxLife: number) {
        this.x = x
        this.y = y
        this.vx = vx
        this.vy = vy
        // Apply Base Size & Chaos
        this.size = Math.max(0.5, CONFIG.baseSize + (Math.random() - 0.5) * CONFIG.sizeVariance)
        this.maxLife = maxLife
        this.life = 0
        this.heat = 1.0
        // Fluidity 0.5 gives a very stable, thick atmosphere (higher drag)
        this.drag = 0.92 + Math.random() * 0.05
    }

    update(df: number = 1) {
        this.x += this.vx * df
        this.y += this.vy * df

        const time = Date.now() * 0.001
        const scale = 0.01 + (CONFIG.fluidity * 0.005)
        const noiseX = Math.sin(this.y * scale + time) + Math.cos(this.x * scale * 0.5)
        const noiseY = Math.cos(this.x * scale - time) + Math.sin(this.y * scale * 0.5)

        this.vx += noiseX * CONFIG.fluidity * 0.05 * df
        this.vy += noiseY * CONFIG.fluidity * 0.05 * df

        this.vy -= 0.02 * CONFIG.fluidity * df

        this.vx *= Math.pow(this.drag, df)
        this.vy *= Math.pow(this.drag, df)

        this.size *= Math.pow(0.97, df)
        this.life += df
        this.heat = 1 - (this.life / this.maxLife)
    }
}

export const createFireballEffect = (sx: number, sy: number, tx: number, ty: number): FireballEffect => {
    return {
        kind: 'fireball',
        t: 0,
        sx,
        sy,
        tx,
        ty
    }
}

// Helper to create the glow sprite
const createGlowSprite = (): HTMLCanvasElement => {
    const sprite = document.createElement('canvas')
    sprite.width = 64
    sprite.height = 64
    const ctx = sprite.getContext('2d')
    if (ctx) {
        const grad = ctx.createRadialGradient(32, 32, 0, 32, 32, 32)
        // Core: Bright Yellow/White mix
        grad.addColorStop(0, 'rgba(255, 240, 150, 1)')
        // Mid: Orange
        grad.addColorStop(0.4, 'rgba(255, 100, 0, 0.5)')
        // Edge: Transparent
        grad.addColorStop(1, 'rgba(255, 50, 0, 0)')
        ctx.fillStyle = grad
        ctx.fillRect(0, 0, 64, 64)
    }
    return sprite
}

export const renderFireballEffect = (
    eff: FireballEffect,
    ctx: CanvasRenderingContext2D
): boolean => {
    // Initialize data if missing
    if (!eff.data) {
        const dx = eff.tx - eff.sx
        const dy = eff.ty - eff.sy
        const angle = Math.atan2(dy, dx)

        const gameSpeed = CONFIG.speed * 4.5

        eff.data = {
            particles: [],
            phase: 'FLYING',
            pos: { x: eff.sx, y: eff.sy },
            velocity: {
                x: Math.cos(angle) * gameSpeed,
                y: Math.sin(angle) * gameSpeed
            },
            target: { x: eff.tx, y: eff.ty },
            sprite: createGlowSprite()
        }
    }

    const d = eff.data
    const sprite = d.sprite
    const df = (eff as any)._df ?? 1

    // --- LOGIC ---

    if (d.phase === 'FLYING') {
        d.pos.x += d.velocity.x * df
        d.pos.y += d.velocity.y * df

        const emitCount = Math.ceil(CONFIG.particleCount / 10 * df)

        for (let i = 0; i < emitCount; i++) {
            const spread = CONFIG.baseSize // 30.0
            const px = d.pos.x + (Math.random() - 0.5) * spread
            const py = d.pos.y + (Math.random() - 0.5) * spread

            // Dragged behind
            const vx = -d.velocity.x * 0.2 + (Math.random() - 0.5)
            const vy = -d.velocity.y * 0.2 + (Math.random() - 0.5)

            d.particles.push(new FluidParticle(px, py, vx, vy, 60))
        }

        // Collision Check
        const dist = Math.hypot(d.target.x - d.pos.x, d.target.y - d.pos.y)
        // Original check: dist < config.speed * 6 (approx 18)
        // We use a slightly larger threshold to ensure it hits
        if (dist < 30) {
            d.phase = 'EXPLODING'
            // Burst
            for (let i = 0; i < CONFIG.particleCount * 4; i++) {
                const a = Math.random() * 6.28
                const s = Math.random() * 15
                const vx = Math.cos(a) * s
                const vy = Math.sin(a) * s
                d.particles.push(new FluidParticle(d.pos.x, d.pos.y, vx, vy, 100))
            }
        }
    }

    // Update Particles
    for (let i = d.particles.length - 1; i >= 0; i--) {
        const p = d.particles[i]
        p.update(df)

        if (p.life >= p.maxLife || p.size < 0.5) {
            d.particles[i] = d.particles[d.particles.length - 1]
            d.particles.pop()
        }
    }

    // --- RENDER ---
    ctx.save()
    ctx.globalCompositeOperation = 'lighter'

    // Draw Particles
    for (const p of d.particles) {
        // Color Interpolation
        const t = p.heat
        const r = CONFIG.tailColor.r + (CONFIG.headColor.r - CONFIG.tailColor.r) * t
        const g = CONFIG.tailColor.g + (CONFIG.headColor.g - CONFIG.tailColor.g) * t
        const b = CONFIG.tailColor.b + (CONFIG.headColor.b - CONFIG.tailColor.b) * t
        const alpha = p.heat

        if (sprite && p.size > 2) {
            ctx.globalAlpha = alpha * 0.4
            ctx.drawImage(sprite, p.x - p.size, p.y - p.size, p.size * 2, p.size * 2)

            // Colored core for tint
            ctx.globalAlpha = alpha * 0.6
            ctx.fillStyle = `rgb(${r}, ${g}, ${b})`
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size * 0.4, 0, Math.PI * 2)
            ctx.fill()
        } else {
            ctx.fillStyle = `rgba(${r},${g},${b},${alpha})`
            ctx.beginPath()
            ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2)
            ctx.fill()
        }
    }

    // Draw Projectile Head (Only in FLYING)
    if (d.phase === 'FLYING' && sprite) {
        ctx.globalAlpha = 1.0
        const s = CONFIG.baseSize * 6
        ctx.drawImage(sprite, d.pos.x - s / 2, d.pos.y - s / 2, s, s)
    }

    ctx.restore()

    // Return true if still active
    return d.particles.length > 0 || d.phase === 'FLYING'
}
